import React, { FunctionComponent, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { RootState } from "../../../redux/reducers/index";
import {
	CartTotalSummaryContainer,
	CartTotalSummaryBody,
	CartTotalSummaryDetail,
	CartSummaryModalMobile,
	CartTotalSummaryTitle,
	ContainerTwoStep,
	CartTotalSummaryAmount,
	CartTotalSummaryTitleContainer,
	CartDetailSummaryContainer,
	DiscountCodeInputContainer,
	DiscountCodeInputField,
	DiscountCodeContainer,
	DiscountCodeLabelContainer,
	DiscountCodeButton,
	DiscountCodeErrorText,
	DiscountCouponsContainer,
	LoadContainer,
} from "../style";
import CurrencyFormat from "../../../utils/currencyFormat";
import { setFooterBottomSpacing } from "../../../redux/actions";
import CartProductSummary from "../CartProductSummary";
import { viewTotalDiscountAmount } from "../../../utils/formatData";
import { language } from "../../../utils/language";
import { isFreeShippingCoupon } from "../../../utils/discountTypes";
import { Button, withStyles } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { styled } from "@mui/system";
import { Label } from "@material-ui/icons";
import Spinner from "../../Spinner";
import { useDiscountCoupons } from "../../../hooks/useDiscountCoupons";

type FindQuoteFunction = (operator: string) => any;

type CartTotalSummaryThreeProps = {
	data: any[];
	publicKey: string;
	clientId: number;
	totalAmount: number;
	subTotalAmount: number;
	shippingAmount: number;
	taxAmountTotal: number;
	consumptionTaxAmountTotal: number;
	cartId: string;
	catalogueId: string;
	color: string;
	isValidForm: boolean;
	loading: boolean;
	successInfo: boolean;
	appliedCoupons: Coupon[];
	setAppliedCoupons: any;
	nextStep?: () => any;
	getFormValues?: () => any;
	findQuote: FindQuoteFunction;
	showDetailOnMobile: boolean;
	carrier?: any;
	freeShippingNotice?: React.ReactNode;
};

const CartTotalSummary: FunctionComponent<CartTotalSummaryThreeProps> = ({
	successInfo,
	cartId,
	catalogueId,
	data,
	clientId,
	color,
	totalAmount,
	subTotalAmount,
	shippingAmount,
	taxAmountTotal,
	consumptionTaxAmountTotal,
	publicKey,
	appliedCoupons,
	setAppliedCoupons,
	loading,
	nextStep = () => {},
	isValidForm = false,
	getFormValues = () => {},
	findQuote,
	showDetailOnMobile,
	carrier,
	freeShippingNotice,
}) => {
	const dispatch = useDispatch();
	const homeState: HomeStateType = useSelector(
		(state: RootState) => state.home
	);
	const aveonlineIntegration: any = useSelector(
		(state: RootState) => state.aveonline.integration
	);
	const hasAveonlineProducts = data.some(
		(item: any) => item.productData?.aveonlineId
	);
	const isAveonlineActive =
		aveonlineIntegration?.status &&
		hasAveonlineProducts &&
		aveonlineIntegration?.wherehouseStatus === "Confirmado";

	const renderShippingCost = () => {
		if (shippingAmount > 0) {
			return <CurrencyFormat amount={shippingAmount} />;
		}
		if (isAveonlineActive) {
			return <CurrencyFormat amount={0} />;
		}
		return language[homeState.default_language]["free"];
	};

	const [items, setItems] = useState<any>([]);

	const {
		couponCode,
		showCouponError,
		couponErrorMessages,
		loadCode,
		handleInputChange,
		handleApplyCoupon,
		handleCloseCoupon,
	} = useDiscountCoupons({
		cartId,
		catalogueId,
		items,
		appliedCoupons,
		setAppliedCoupons,
		subTotalAmount,
		taxAmountTotal,
		consumptionTaxAmountTotal,
	});

	const StyledArrowForwardIcon = styled(ArrowForwardIcon)`
		color: #ffffff;
		margin: 0 auto;
	`;

	const DiscountButton = withStyles((theme) => ({
		root: {
			borderRadius: "0px",
			display: "block !important",
			height: "35.2px",
			color: "#FFFFFF",
			left: "-4px",
			background: homeState.color,
			borderTopLeftRadius: "0",
			borderTopRightRadius: "10px",
			borderBottomLeftRadius: "0",
			borderBottomRightRadius: "10px",

			".MuiSvgIcon-root": {
				margin: "0 auto",
				color: "#FFFFFF",
			},

			"&:hover": {
				background: "#FFFFFF",
				border: `1px solid ${homeState.color}`,
				"& .MuiSvgIcon-root ": {
					color: `${homeState.color} !important`,
				},
			},
		},
		label: {
			margin: 0,
		},
	}))(Button);

	const [windowWidth, setWindowWidth] = useState(window.innerWidth);

	useEffect(() => {
		const productsRefs = data.filter(
			(item: any) => item.references && item.references.length > 0
		);
		const productsData = data.filter(
			(item: any) => !(item.references && item.references.length > 0)
		);
		if (productsRefs.length > 0) {
			productsRefs.map((product: any) => {
				product.references.map((itemRef: any) => {
					itemRef.data = product.productData.references.find(
						(field: any) => field.id === itemRef.id
					);
					productsData.push({ ...product, references: [itemRef] });
				});
			});
		}
		setItems(productsData);
	}, [data]);

	useEffect(() => {
		dispatch(setFooterBottomSpacing("3.5rem"));
		return () => {
			dispatch(setFooterBottomSpacing(undefined));
		};
	}, []);

	useEffect(() => {
		const handleResize = () => {
			setWindowWidth(window.innerWidth);
		};

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	return (
		<CartTotalSummaryContainer
			style={{
				display: windowWidth > 768 || showDetailOnMobile ? "flex" : "none",
			}}
		>
			<CartTotalSummaryBody position="block">
				<ContainerTwoStep>
					<CartSummaryModalMobile show={showDetailOnMobile}>
						<div className="step-three-spacer" style={{ marginBottom: "2rem" }}>
							<CartTotalSummaryTitleContainer>
								<CartProductSummary
									data={data}
									color={color}
									loading={loading}
									appliedCoupons={appliedCoupons}
									totalAmount={0}
								/>
							</CartTotalSummaryTitleContainer>
						</div>
						<CartDetailSummaryContainer>
							<DiscountCodeContainer>
								<DiscountCodeInputContainer>
									<DiscountCodeInputField
										color={homeState.color}
										error={couponErrorMessages.length > 0}
										id="discount-code-input-step-three"
										type="text"
										placeholder={
											language[homeState.default_language]["codeDiscount"]
										}
										value={couponCode}
										onChange={handleInputChange}
									/>
									<DiscountButton
										id="discount-code-button-step-three"
										onClick={handleApplyCoupon}
										variant="contained"
										disableElevation
									>
										<StyledArrowForwardIcon />
									</DiscountButton>
								</DiscountCodeInputContainer>

								<DiscountCouponsContainer>
									{appliedCoupons.length > 0 && (
										<div className="discount_labels">
											{appliedCoupons.map((coupon, index) => (
												<DiscountCodeLabelContainer
													key={index}
													color={homeState.color}
													className={
														showCouponError && coupon.error ? "error" : ""
													}
												>
													<Label
														className={`discount-code__label-icon ${
															showCouponError && coupon.error ? "error" : ""
														}`}
													/>
													<span
														className={`discount-code__name ${
															showCouponError && coupon.error ? "error" : ""
														}`}
													>
														{coupon.code}
													</span>
													<span
														className={`discount-code__discount-amount ${
															showCouponError && coupon.error ? "error" : ""
														}`}
													>
														(
														{isFreeShippingCoupon(coupon) ? (
															language[homeState.default_language]["freeShipping"]
														) : coupon.type !== "monto fijo" ? (
															`${coupon.value}%`
														) : (
															<CurrencyFormat amount={coupon.value} />
														)}
														)
													</span>
													<DiscountCodeButton
														className={
															showCouponError && coupon.error ? "error" : ""
														}
														color={homeState.color}
														onClick={() => handleCloseCoupon(index)}
													>
														X
													</DiscountCodeButton>
												</DiscountCodeLabelContainer>
											))}
										</div>
									)}

									<div>
										{couponErrorMessages.length > 0 && (
											<div className="error-messages">
												{couponErrorMessages.map((errorMessage, index) => (
													<DiscountCodeErrorText key={index}>
														{errorMessage}
													</DiscountCodeErrorText>
												))}
											</div>
										)}
									</div>
								</DiscountCouponsContainer>
							</DiscountCodeContainer>
						</CartDetailSummaryContainer>
						{loadCode && (
							<LoadContainer>
								<Spinner color={homeState.color} />
							</LoadContainer>
						)}
						<CartTotalSummaryTitleContainer>
							<CartTotalSummaryTitle mp="10px">

								{language[homeState.default_language]["summary"]}
							</CartTotalSummaryTitle>
						</CartTotalSummaryTitleContainer>
						<CartTotalSummaryDetail>
							{taxAmountTotal + consumptionTaxAmountTotal === 0 && (
								<>
									<div className="cart-total-summary__title-container">
										<span className="cart-total-summary__title">
											{language[homeState.default_language]["subtotal"]}
										</span>
										<span className="cart-total-summary__title">
											<CurrencyFormat amount={Math.abs(subTotalAmount)} />
										</span>
									</div>
									<div className="cart-total-summary__title-container">
										<span className="cart-total-summary__title">
												{language[homeState.default_language]["cost"]}{" "}
												{language[homeState.default_language]["send"]}
										</span>
										<span className="cart-total-summary__title">
												{renderShippingCost()}
										</span>
									</div>
								</>
							)}
							{taxAmountTotal + consumptionTaxAmountTotal !== 0 && (
								<>
									<div className="cart-total-summary__title-container">
										<span className="cart-total-summary__title">
											{language[homeState.default_language]["subtotal"]} +{" "}
											{language[homeState.default_language]["tax2"]}
										</span>
										<span className="cart-total-summary__title">
											<CurrencyFormat
												amount={Math.abs(
													subTotalAmount +
														taxAmountTotal +
														consumptionTaxAmountTotal
												)}
											/>
										</span>
									</div>
									<div className="cart-total-summary__title-container">
										<span className="cart-total-summary__title">
											{language[homeState.default_language]["cost"]}{" "}
											{language[homeState.default_language]["send"]}
										</span>
										<span className="cart-total-summary__title">
												{renderShippingCost()}
										</span>
									</div>
								</>
							)}
							{viewTotalDiscountAmount(
								items,
								appliedCoupons,
								subTotalAmount + taxAmountTotal + consumptionTaxAmountTotal,
								"amount",
								"COP",
								homeState.default_language
							) > 0 && (
								<div className="cart-total-summary__title-container">
									<span className="cart-total-summary__title">
										{language[homeState.default_language]["discount"]}
									</span>
									<span
										className="cart-total-summary__title"
										style={{ color: "#67C940" }}
									>
										-{" "}
										<CurrencyFormat
											amount={viewTotalDiscountAmount(
												items,
												appliedCoupons,
												subTotalAmount +
													taxAmountTotal +
													consumptionTaxAmountTotal,
												"amount",
												"COP",
												homeState.default_language
											)}
										/>
									</span>
								</div>
							)}
							{
								homeState.epaycoDeliveryProvider &&
								!homeState.freeDelivery &&
								findQuote("472") !== null && (
								<div style={{display:"none"}} className="cart-total-summary__title-container">
								<span className="cart-total-summary__title">
									{language[homeState.default_language]["send"]}
								</span>

								<div>
										<span className="cart-total-summary__title">
											<CurrencyFormat
												amount={Math.abs(findQuote("472")?.valor)}
											/>
										</span>
									</div>
							</div>
						)}
						</CartTotalSummaryDetail>
						{freeShippingNotice}
						<CartTotalSummaryAmount>
							<div id="title">Total</div>
							<div id="amount" style={{ color: `${homeState.color}` }}>
								<CurrencyFormat
									amount={
										subTotalAmount +
										taxAmountTotal +
										consumptionTaxAmountTotal +
										(carrier || !homeState.freeDelivery ? shippingAmount : 0) -
										parseFloat(
											viewTotalDiscountAmount(
												items,
												appliedCoupons,
												subTotalAmount +
													taxAmountTotal +
													consumptionTaxAmountTotal,
												"amount",
												"COP",
												homeState.default_language
											).toString()
										)
									}
								/>
							</div>
						</CartTotalSummaryAmount>
					</CartSummaryModalMobile>
				</ContainerTwoStep>
			</CartTotalSummaryBody>
		</CartTotalSummaryContainer>
	);
};

export default CartTotalSummary;
